/**
 * ClubAdminMembers — full member admin: list · search · authorise · CSV import/export.
 * Plus single-member edit/add.
 *
 * Path: /club/admin/members  ·  /club/admin/members/:card_no
 * Auth: /mira/console (lc_console_token).
 *
 * FIR-20260611-001 · §6 of KVB-ENTRY-INSTRUCTIONS
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useConsoleAuth } from "@/lib/console-auth";
import ClubLayout from "./ClubLayout";
import * as M from "./memberApi";

const TENANT_OPTIONS = ["global", "kvb", "lesconcierges"];
const STATUS_OPTIONS = ["active", "inactive", "pending_review"];

/* ─── List view ──────────────────────────────────────── */

export function ClubAdminMembersList() {
  const { user, loading: authLoading, logout } = useConsoleAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ tenant: "", authorised: "", status: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/mira/console/login?return=/club/admin/members", { replace: true });
      return;
    }
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  async function fetchList() {
    setLoading(true); setError(null);
    try {
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (filter.tenant) params.tenant = filter.tenant;
      if (filter.authorised !== "") params.authorised = filter.authorised;
      if (filter.status) params.status = filter.status;
      const r = await M.listMembers(params);
      setItems(r.items); setTotal(r.total);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }

  function toggleSel(card) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(card)) n.delete(card); else n.add(card);
      return n;
    });
  }
  function selectAllVisible() {
    setSelected(new Set(items.map((m) => m.membership_card_no)));
  }
  function clearSel() { setSelected(new Set()); }

  async function bulkAuth(state) {
    if (selected.size === 0) return;
    setBusy(true); setMsg(null);
    try {
      const r = await M.bulkAuthorise(Array.from(selected), state);
      setMsg({ ok: true, text: `${state ? "Authorised" : "De-authorised"} ${r.modified} member${r.modified === 1 ? "" : "s"}.` });
      await fetchList();
      clearSel();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function onUploadCsv(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      const r = await M.importMembersCsv(file, filter.tenant || "");
      setMsg({
        ok: true,
        text: `Import done · added ${r.added} · updated ${r.updated} · skipped ${r.skipped_protected_fields} protected fields · ${r.errors} errors.`,
      });
      await fetchList();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const exportUrl = useMemo(() => M.exportMembersCsvUrl(filter.tenant, filter.authorised),
    [filter.tenant, filter.authorised]);

  if (authLoading || !user) {
    return (
      <ClubLayout member={null}>
        <main className="wrap"><div className="loading">Checking access…</div></main>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout member={{ first: user.first || "Admin", last: user.last || "" }} onSignOut={logout}>
      <main className="wrap" style={{ paddingTop: 30, paddingBottom: 80 }}>
        <section className="hero" style={{ padding: "16px 0 24px" }}>
          <div className="kicker">MindEscapes® · Admin · Members</div>
          <h1 className="serif" style={{ fontSize: "clamp(28px, 4vw, 44px)" }}>
            Membership directory
          </h1>
          <p>Add, edit, import &amp; authorise. Members default to <b>unauthorised</b> on import — flip the toggle when KVB confirms.</p>
          <div style={{ display: "flex", gap: 12, marginTop: 18, alignItems: "center", flexWrap: "wrap" }}>
            <Link to="/club/admin/members/new" className="btn btn-primary"
                  style={{ padding: "10px 22px", textDecoration: "none" }}
                  data-testid="admin-mem-add-btn">+ Add member</Link>
            <label className="btn btn-ghost" style={{ cursor: "pointer", padding: "10px 16px" }} data-testid="admin-mem-import-label">
              ↑ Import CSV
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }}
                     onChange={onUploadCsv} data-testid="admin-mem-import-input" />
            </label>
            <a href={exportUrl} className="btn btn-ghost" style={{ padding: "10px 16px", textDecoration: "none" }}
               data-testid="admin-mem-export-btn">↓ Export CSV</a>
            <Link to="/club/admin/clubs" style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>→ Clubs</Link>
            <Link to="/club/admin/requests" style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>→ Requests queue</Link>
          </div>
        </section>

        {msg && (
          <div data-testid="admin-mem-msg" style={{
            padding: "10px 14px", borderRadius: 10, marginBottom: 14, fontSize: 13.5,
            background: msg.ok ? "rgba(127,174,60,0.16)" : "rgba(180,68,31,0.16)",
            color: msg.ok ? "#3d6010" : "#7a2c12",
            border: `1px solid ${msg.ok ? "rgba(127,174,60,0.4)" : "rgba(180,68,31,0.4)"}`,
          }}>{msg.text}</div>
        )}

        <section className="filter-block">
          <div className="search-row" style={{ marginBottom: 12 }}>
            <div className="search" style={{ flex: 1 }}>
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
              <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") fetchList(); }}
                     placeholder="Search name / card no / email / city · Enter to apply"
                     data-testid="admin-mem-search" />
            </div>
            <button className="btn btn-primary" style={{ padding: "0 24px", height: 54 }} onClick={fetchList} data-testid="admin-mem-apply">
              Apply
            </button>
          </div>
          <div className="frow">
            <span className="flabel">Tenant</span>
            <select className="country-sel" value={filter.tenant}
                    onChange={(e) => setFilter((f) => ({ ...f, tenant: e.target.value }))}
                    data-testid="admin-mem-tenant-sel">
              <option value="">All</option>
              {TENANT_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="flabel">Authorised</span>
            <select className="country-sel" value={filter.authorised}
                    onChange={(e) => setFilter((f) => ({ ...f, authorised: e.target.value }))}
                    data-testid="admin-mem-auth-sel">
              <option value="">Any</option>
              <option value="true">Authorised</option>
              <option value="false">Unauthorised</option>
            </select>
            <span className="flabel">Status</span>
            <select className="country-sel" value={filter.status}
                    onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
              <option value="">Any</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </section>

        {selected.size > 0 && (
          <div style={{
            position: "sticky", top: 84, zIndex: 30,
            background: "rgba(46,157,157,0.12)", border: "1px solid var(--teal)",
            borderRadius: 10, padding: "10px 16px", display: "flex",
            alignItems: "center", gap: 14, marginBottom: 14, flexWrap: "wrap",
          }} data-testid="admin-mem-bulk-bar">
            <b style={{ color: "var(--teal-d)" }}>{selected.size}</b> selected
            <button className="btn btn-primary" style={{ padding: "8px 16px", fontSize: 13 }}
                    onClick={() => bulkAuth(true)} disabled={busy}
                    data-testid="admin-mem-bulk-auth-on">
              Authorise
            </button>
            <button className="btn btn-ghost" style={{ padding: "8px 16px", fontSize: 13 }}
                    onClick={() => bulkAuth(false)} disabled={busy}
                    data-testid="admin-mem-bulk-auth-off">
              De-authorise
            </button>
            <button className="btn btn-ghost" style={{ padding: "8px 14px", fontSize: 13 }}
                    onClick={clearSel}>Clear</button>
          </div>
        )}

        {error && <div className="empty"><div className="serif">Error</div>{error}</div>}

        {loading ? (
          <div className="loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="empty"><div className="serif">No members yet.</div>Use “Add member” or “Import CSV”.</div>
        ) : (
          <div style={{
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 14, overflow: "hidden", boxShadow: "var(--shadow)",
          }}>
            <div style={{ padding: "12px 18px", borderBottom: "1px solid var(--line-2)", color: "var(--muted)", fontSize: 13, display: "flex", justifyContent: "space-between" }}>
              <div>Showing <b style={{ color: "var(--ink)" }}>{items.length}</b> of <b style={{ color: "var(--ink)" }}>{total}</b></div>
              <button className="clear-btn" onClick={selectAllVisible} data-testid="admin-mem-select-all">Select all visible</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }} data-testid="admin-mem-table">
                <thead>
                  <tr style={{ background: "rgba(35,48,42,0.04)", textAlign: "left" }}>
                    {["", "Card", "Name", "Resident", "Phone / Email", "Tenant", "Auth", "Status", ""].map((h, i) => (
                      <th key={i} style={{ padding: "11px 12px", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => {
                    const card = m.membership_card_no;
                    return (
                      <tr key={card} data-testid={`admin-mem-row-${card}`}
                          style={{ borderTop: "1px solid var(--line-2)" }}>
                        <td style={{ padding: "10px 12px", width: 36 }}>
                          <input type="checkbox"
                                 checked={selected.has(card)}
                                 onChange={() => toggleSel(card)}
                                 data-testid={`admin-mem-check-${card}`} />
                        </td>
                        <td style={{ padding: "10px 12px", fontFamily: "ui-monospace, Menlo, monospace", color: "var(--teal-d)" }}>
                          {card}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <b>{m.first_name} {m.last_name}</b>
                          {m.spouse_name && <div style={{ fontSize: 11.5, color: "var(--muted)" }}>+ spouse: {m.spouse_name}</div>}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {m.resident_city || "—"}{m.resident_state ? `, ${m.resident_state}` : ""}
                        </td>
                        <td style={{ padding: "10px 12px", fontSize: 12 }}>
                          <div>{m.mobile || "—"}</div>
                          <div style={{ color: "var(--muted)" }}>{m.email || "—"}</div>
                        </td>
                        <td style={{ padding: "10px 12px" }}>{m.tenant || "global"}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{
                            background: m.authorised ? "rgba(127,174,60,0.18)" : "rgba(160,122,78,0.18)",
                            color: m.authorised ? "#3d6010" : "#7a532c",
                            padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                          }} data-testid={`admin-mem-authpill-${card}`}>
                            {m.authorised ? "AUTHORISED" : "HELD"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {m.member_status === "pending_review" ? (
                            <span style={{
                              background: "rgba(216,153,54,0.18)", color: "#7a532c",
                              padding: "3px 9px", borderRadius: 100, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                            }} data-testid={`admin-mem-pending-${card}`}>
                              PENDING REVIEW
                            </span>
                          ) : (
                            <span style={{ color: m.member_status === "active" ? "var(--ink)" : "var(--muted)", fontSize: 12.5 }}>
                              {m.member_status || "active"}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <Link to={`/club/admin/members/${encodeURIComponent(card)}`}
                                data-testid={`admin-mem-edit-${card}`}
                                style={{ color: "var(--teal-d)", fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 2 }}>
                            Edit
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </ClubLayout>
  );
}

/* ─── Edit / Create view ─────────────────────────────── */

const NEW_DEFAULTS = {
  first_name: "", last_name: "", membership_card_no: "",
  email: "", mobile: "", address: "",
  resident_city: "", resident_state: "", resident_pincode: "",
  spouse_name: "", children_names: [],
  tenant: "kvb", tier: "",
  authorised: false, member_status: "active",
  photo_url: "", spouse_photo_url: "",
};

export function ClubAdminMemberEdit() {
  const { card_no } = useParams();
  const isNew = card_no === "new";
  const { user, loading: authLoading, logout } = useConsoleAuth();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(isNew ? NEW_DEFAULTS : null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [childInput, setChildInput] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/mira/console/login?return=/club/admin/members${isNew ? "/new" : "/" + card_no}`, { replace: true });
      return;
    }
    (async () => {
      try {
        if (!isNew) {
          const m = await M.getMember(card_no);
          setMember(m);
          setDraft({ ...NEW_DEFAULTS, ...m });
        }
      } catch (e) {
        setMsg({ ok: false, text: e.response?.data?.detail || e.message });
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card_no, user, authLoading]);

  function set(k, v) { setDraft((d) => ({ ...d, [k]: v })); }

  function addChild() {
    const v = (childInput || "").trim();
    if (!v) return;
    setDraft((d) => ({ ...d, children_names: [...(d.children_names || []), v] }));
    setChildInput("");
  }
  function removeChild(i) {
    setDraft((d) => ({ ...d, children_names: d.children_names.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    setSaving(true); setMsg(null);
    try {
      const payload = { ...draft };
      // Drop empty email so backend doesn't fail Pydantic email validation
      if (!payload.email) delete payload.email;
      if (isNew) {
        const r = await M.createMember(payload);
        setMsg({ ok: true, text: "Member created." });
        setTimeout(() => navigate(`/club/admin/members/${r.member.membership_card_no}`, { replace: true }), 500);
      } else {
        const r = await M.patchMember(card_no, payload);
        setMember(r.member);
        // FIR-20260623-CardNoEdit · if the admin renamed the card number,
        // the URL still references the OLD card no — re-route to the new
        // one so subsequent PATCH/photo calls hit the right document.
        const newCard = r?.member?.membership_card_no || r?.card_no;
        if (newCard && newCard !== card_no) {
          setMsg({ ok: true, text: `Saved · card number changed to ${newCard}.` });
          setTimeout(() => navigate(`/club/admin/members/${encodeURIComponent(newCard)}`, { replace: true }), 500);
        } else {
          setMsg({ ok: true, text: `Saved · ${r.changed} field${r.changed === 1 ? "" : "s"}.` });
        }
      }
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function softDelete() {
    const reason = window.prompt("Soft-delete reason (e.g. 'lapsed'):");
    if (reason === null) return;
    setSaving(true); setMsg(null);
    try {
      await M.deleteMember(card_no, { reason: reason || "admin_soft_delete" });
      setMsg({ ok: true, text: "Member soft-deleted (inactive + un-authorised)." });
      setTimeout(() => navigate("/club/admin/members"), 700);
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }
  async function hardDelete() {
    if (!window.confirm("PERMANENT delete — visit history will be lost. Continue?")) return;
    if (window.prompt('Type "DELETE" to confirm:') !== "DELETE") return;
    setSaving(true);
    try {
      await M.deleteMember(card_no, { hard: true });
      navigate("/club/admin/members");
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || e.message });
      setSaving(false);
    }
  }

  if (authLoading || !user || loading) {
    return <ClubLayout member={null}><main className="wrap"><div className="loading">Loading…</div></main></ClubLayout>;
  }
  if (!draft) {
    return <ClubLayout member={null}><main className="wrap"><div className="empty"><div className="serif">Member not found</div></div></main></ClubLayout>;
  }

  return (
    <ClubLayout member={{ first: user.first || "Admin", last: user.last || "" }} onSignOut={logout}>
      <main className="wrap" style={{ paddingTop: 24, paddingBottom: 80 }}>
        <section className="hero" style={{ padding: "10px 0 18px" }}>
          <Link to="/club/admin/members" style={{ color: "var(--teal-d)", fontSize: 13, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>← back to members</Link>
          <h1 className="serif" style={{ fontSize: "clamp(24px, 3.4vw, 36px)", marginTop: 6 }}>
            {isNew ? "Add a new member" : `${draft.first_name} ${draft.last_name}`}
          </h1>
          {!isNew && member && (
            <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, fontFamily: "ui-monospace, Menlo, monospace" }}>
              card {card_no} · tenant {draft.tenant || "global"}
              {(draft.founder_edited_fields || []).length > 0 && ` · ${(draft.founder_edited_fields || []).length} edited field(s)`}
            </div>
          )}
        </section>

        {msg && (
          <div data-testid="admin-mem-edit-msg" style={{
            padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 13.5,
            background: msg.ok ? "rgba(127,174,60,0.16)" : "rgba(180,68,31,0.16)",
            color: msg.ok ? "#3d6010" : "#7a2c12",
            border: `1px solid ${msg.ok ? "rgba(127,174,60,0.4)" : "rgba(180,68,31,0.4)"}`,
          }}>{msg.text}</div>
        )}

        <div style={card()}>
          <Section title="Identity · must match Aadhar">
            <Row>
              <Field label="First name (as per Aadhar) *" value={draft.first_name} onChange={(v) => set("first_name", v)} testid="admin-mem-first" />
              <Field label="Last name (as per Aadhar) *" value={draft.last_name} onChange={(v) => set("last_name", v)} testid="admin-mem-last" />
            </Row>
            <Row>
              <Field label="Membership card no *"
                     value={draft.membership_card_no}
                     onChange={(v) => set("membership_card_no", v)}
                     testid="admin-mem-card" />
              <Select label="Tenant" value={draft.tenant} options={TENANT_OPTIONS} onChange={(v) => set("tenant", v)} />
              <Field label="Tier / card type" value={draft.tier || ""} onChange={(v) => set("tier", v)} />
            </Row>
          </Section>
          <Section title="Contact">
            <Row>
              <Field label="Email" value={draft.email || ""} onChange={(v) => set("email", v)} type="email" testid="admin-mem-email" />
              <Field label="Mobile *" value={draft.mobile || ""} onChange={(v) => set("mobile", v)} testid="admin-mem-mobile" />
            </Row>
            <Row>
              <Field label="Address" value={draft.address || ""} onChange={(v) => set("address", v)} full multiline rows={2} />
            </Row>
            <p style={{ fontSize: 11.5, color: "var(--muted)", margin: "0 0 6px", fontStyle: "italic" }}>
              Residence details below — must match Aadhar (used for domicile-club eligibility).
            </p>
            <Row>
              <Field label="City of residence (as per Aadhar) *" value={draft.resident_city || ""} onChange={(v) => set("resident_city", v)} testid="admin-mem-city" />
              <Field label="State (as per Aadhar)" value={draft.resident_state || ""} onChange={(v) => set("resident_state", v)} testid="admin-mem-state" />
              <Field label="Pincode (as per Aadhar)" value={draft.resident_pincode || ""} onChange={(v) => set("resident_pincode", v)} />
            </Row>
          </Section>
          <Section title="Family">
            <Row>
              <Field label="Spouse name" value={draft.spouse_name || ""} onChange={(v) => set("spouse_name", v)} />
            </Row>
            <Row>
              <div style={{ flex: "1 1 100%" }}>
                <label style={labelStyle()}>Dependent children (max 2 per visit)</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                  {(draft.children_names || []).map((c, i) => (
                    <span key={i} style={{
                      background: "rgba(46,157,157,0.12)", color: "var(--teal-d)",
                      padding: "5px 10px", borderRadius: 100, fontSize: 12.5, fontWeight: 600,
                      display: "inline-flex", alignItems: "center", gap: 8,
                    }}>
                      {c}
                      <button type="button" onClick={() => removeChild(i)} aria-label="Remove"
                              style={{ background: "none", border: "none", color: "var(--teal-d)", cursor: "pointer", fontSize: 14 }}>×</button>
                    </span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input style={input()} value={childInput}
                         onChange={(e) => setChildInput(e.target.value)}
                         onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addChild(); } }}
                         placeholder="Add child name + Enter" />
                  <button className="btn btn-ghost" style={{ padding: "10px 16px" }} onClick={addChild}>Add</button>
                </div>
              </div>
            </Row>
          </Section>
          {!isNew && (
            <Section title="Photos">
              <p style={{ fontSize: 11.5, color: "var(--muted)", margin: "0 0 12px", fontStyle: "italic" }}>
                Member photo is required. Spouse photo only when a spouse is recorded. Stored on Cloudinary.
              </p>
              <Row>
                <PhotoSlot
                  cardNo={card_no}
                  slot="self"
                  label="Member · self"
                  url={draft.photo_url}
                  onChange={(url) => set("photo_url", url)}
                  onError={(text) => setMsg({ ok: false, text })}
                />
                {(draft.spouse_name || "").trim() ? (
                  <PhotoSlot
                    cardNo={card_no}
                    slot="spouse"
                    label={`Spouse · ${draft.spouse_name}`}
                    url={draft.spouse_photo_url}
                    onChange={(url) => set("spouse_photo_url", url)}
                    onError={(text) => setMsg({ ok: false, text })}
                  />
                ) : (
                  <div style={{ flex: "1 1 200px", padding: 14, border: "1px dashed var(--line)", borderRadius: 10, color: "var(--muted)", fontSize: 12.5, fontStyle: "italic" }}>
                    Enter spouse name above to enable spouse photo upload.
                  </div>
                )}
              </Row>
            </Section>
          )}
          {isNew && (
            <Section title="Photos">
              <p style={{ fontSize: 12, color: "var(--muted)", margin: 0, fontStyle: "italic" }}>
                Save the member first, then upload self &amp; spouse photos on the edit screen.
              </p>
            </Section>
          )}
          <Section title="Access · key">
            <Row>
              <Toggle label="Authorised (allows gate access)"
                      value={!!draft.authorised}
                      onChange={(v) => set("authorised", v)}
                      testid="admin-mem-authorised" />
              <Select label="Member status" value={draft.member_status || "active"}
                      options={STATUS_OPTIONS}
                      onChange={(v) => set("member_status", v)}
                      testid="admin-mem-status" />
            </Row>
            <p style={{ fontSize: 12, color: "var(--muted)", margin: 0 }}>
              Membership gate only lets in members where <b>authorised == true AND status == active</b>.
            </p>
          </Section>

          {!isNew && (
            <Section title="Welcome email">
              <WelcomeEmailBlock
                cardNo={card_no}
                draft={draft}
                onSent={(at) => {
                  setMsg({ ok: true, text: `Welcome email sent to ${draft.email} at ${new Date(at).toLocaleString()}.` });
                  setDraft((d) => ({ ...d, welcome_email_sent_at: at }));
                }}
                onError={(text) => setMsg({ ok: false, text })}
              />
            </Section>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
            <button className="btn btn-primary" onClick={save} disabled={saving}
                    data-testid="admin-mem-save-btn">
              {saving ? "Saving…" : isNew ? "Create member" : "Save changes"}
            </button>
            {!isNew && (
              <>
                <button className="btn btn-ghost" onClick={softDelete} disabled={saving}>Soft-delete</button>
                <button className="btn btn-ghost" onClick={hardDelete} disabled={saving}
                        style={{ borderColor: "#b4441f", color: "#7a2c12" }}>Hard delete</button>
              </>
            )}
          </div>
        </div>
      </main>
    </ClubLayout>
  );
}

/* ─── Micro components ──────────────────────────────── */
function card() {
  return { background: "var(--card)", border: "1px solid var(--line)", borderRadius: 14, padding: "20px 22px", boxShadow: "var(--shadow)" };
}
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <h4 style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, marginBottom: 12 }}>{title}</h4>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>{children}</div>;
}
function labelStyle() {
  return { fontSize: 10.5, letterSpacing: "0.09em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 700, marginBottom: 4, display: "block" };
}
function input() {
  return { border: "1px solid var(--line)", borderRadius: 10, background: "var(--bg)", padding: "10px 12px", fontFamily: "'Archivo', sans-serif", fontSize: 14, color: "var(--ink)", width: "100%", boxSizing: "border-box" };
}
function Field({ label, value, onChange, full, multiline, rows = 1, testid, type = "text", disabled }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: full ? "1 1 100%" : "1 1 200px", minWidth: 0 }}>
      <label style={labelStyle()}>{label}</label>
      {multiline
        ? <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} style={input()} disabled={disabled} data-testid={testid} />
        : <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} style={{ ...input(), opacity: disabled ? 0.6 : 1 }} disabled={disabled} data-testid={testid} />
      }
    </div>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 200px" }}>
      <label style={labelStyle()}>{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} style={input()} data-testid={testid}>
        <option value=""></option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function Toggle({ label, value, onChange, testid }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 200px" }}>
      <label style={labelStyle()}>{label}</label>
      <button type="button" onClick={() => onChange(!value)} data-testid={testid}
              style={{ ...input(), background: value ? "rgba(46,157,157,0.15)" : "var(--bg)",
                       borderColor: value ? "var(--teal)" : "var(--line)",
                       color: value ? "var(--teal-d)" : "var(--ink)",
                       fontWeight: 600, cursor: "pointer", textAlign: "left" }}>
        {value ? "✓ Yes" : "Off"}
      </button>
    </div>
  );
}

function WelcomeEmailBlock({ cardNo, draft, onSent, onError }) {
  const [busy, setBusy] = React.useState(false);
  const isPending = (cardNo || "").startsWith("PENDING-");
  const isAuth = !!draft.authorised && (draft.member_status || "active") === "active";
  const hasEmail = !!(draft.email || "").trim();
  const sentAt = draft.welcome_email_sent_at;

  const blockers = [];
  if (isPending) blockers.push("Allocate a real membership card number first (currently still PENDING-)");
  if (!isAuth) blockers.push("Member must be Authorised AND status=Active");
  if (!hasEmail) blockers.push("Member has no email on file");

  async function onSend() {
    const confirmMsg = sentAt
      ? `A welcome email was already sent on ${new Date(sentAt).toLocaleString()}. Send again?`
      : `Send the welcome email to ${draft.email}?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await M.sendWelcomeEmail(cardNo);
      onSent(r.sent_at);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.zoho_send_failed?.error || err.message);
      onError(`Welcome email failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-mem-welcome-block">
      <p style={{ fontSize: 12.5, color: "var(--muted)", margin: "0 0 12px", lineHeight: 1.55 }}>
        Once you've allocated a card number and authorised the member, send the welcome email
        with their card number and sign-in instructions. Delivered via Zoho Desk (replies on the
        original application ticket when available, so the conversation stays threaded).
      </p>
      {sentAt && (
        <div style={{ fontSize: 12.5, padding: "8px 12px", borderRadius: 8, marginBottom: 10,
                      background: "rgba(46,157,157,0.10)", color: "var(--teal-d)",
                      border: "1px solid rgba(46,157,157,0.25)" }}
             data-testid="admin-mem-welcome-sent-stamp">
          ✓ Last sent: {new Date(sentAt).toLocaleString()}
        </div>
      )}
      {blockers.length > 0 && (
        <ul style={{ fontSize: 12.5, color: "#7a2c12", margin: "0 0 10px 18px", lineHeight: 1.6 }}>
          {blockers.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      <button type="button" className="btn btn-primary"
              onClick={onSend}
              disabled={busy || blockers.length > 0}
              data-testid="admin-mem-send-welcome-btn"
              style={{ padding: "10px 18px", fontSize: 13.5 }}>
        {busy ? "Sending…" : sentAt ? "Resend welcome email" : "Send welcome email"}
      </button>
    </div>
  );
}

function PhotoSlot({ cardNo, slot, label, url, onChange, onError }) {
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);

  async function onPick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const r = await M.uploadMemberPhoto(cardNo, slot, file);
      onChange(r.url);
    } catch (err) {
      onError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  async function onRemove() {
    if (!window.confirm(`Remove ${slot} photo?`)) return;
    setBusy(true);
    try {
      await M.deleteMemberPhoto(cardNo, slot);
      onChange(null);
    } catch (err) {
      onError(err.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  }

  return (
    <div style={{ flex: "1 1 200px", border: "1px solid var(--line)", borderRadius: 10, padding: 12, background: "var(--bg)" }}
         data-testid={`admin-mem-photo-${slot}`}>
      <div style={{ ...labelStyle(), marginBottom: 8 }}>{label}</div>
      <div style={{ width: "100%", aspectRatio: "1 / 1", maxHeight: 180, borderRadius: 8, overflow: "hidden", background: "var(--card)",
                    border: "1px solid var(--line-2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
        {url ? (
          <img src={url} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ color: "var(--muted)", fontSize: 12, fontStyle: "italic" }}>No photo yet</span>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <label className="btn btn-ghost" style={{ cursor: "pointer", padding: "8px 12px", fontSize: 12 }}>
          {url ? "Replace" : "Upload"}
          <input ref={fileRef} type="file" accept="image/*" onChange={onPick} disabled={busy}
                 style={{ display: "none" }} data-testid={`admin-mem-photo-${slot}-input`} />
        </label>
        {url && (
          <button type="button" className="btn btn-ghost" onClick={onRemove} disabled={busy}
                  style={{ padding: "8px 12px", fontSize: 12, borderColor: "#b4441f", color: "#7a2c12" }}
                  data-testid={`admin-mem-photo-${slot}-remove`}>
            Remove
          </button>
        )}
        {busy && <span style={{ fontSize: 11.5, color: "var(--muted)", alignSelf: "center" }}>Uploading…</span>}
      </div>
    </div>
  );
}
